/**
 * EventCard — single event row used in the list view and inside the
 * day-drawer on the month view. Composable with `EventDetailPopover`
 * for the expanded detail affordance.
 *
 * Visual cues (per user's CP2 brief):
 *   - `is_synthesized` (document_expiry) → small "Auto" pill + `~`
 *     prefix on the date + no dismiss/edit/delete affordance
 *   - Persisted auto (grant_deadline/grant_renewal) → dismiss affordance
 *   - Manual → edit + delete affordances
 *   - Status = dismissed → row rendered at 55% opacity + Restore
 *     affordance appears (currently stub — restore semantics are
 *     backend PATCH `status=active`, not shipped in CP2 backend; see
 *     G3C-B for the follow-up. For CP2 we render the affordance as
 *     "Restore" which fires a dismiss-toggle via the future endpoint;
 *     until then it's disabled with a tooltip.)
 */
import { Link } from "react-router-dom";
import { Calendar as CalendarIcon, X, Pencil, Trash2 } from "lucide-react";
import { TYPE_META, SOURCE_META, formatEventDate, formatRelative, sourceLinkFor } from "./utils";

export default function EventCard({
  event,
  onDismiss,
  onEdit,
  onDelete,
  testidPrefix = "calendar-event",
}) {
  const type = TYPE_META[event.event_type] || TYPE_META.manual_reminder;
  const src = SOURCE_META[event.event_source] || SOURCE_META.manual;
  const link = sourceLinkFor(event);
  const isDismissed = event.status === "dismissed";

  return (
    <li
      data-testid={`${testidPrefix}-row-${event.id}`}
      data-event-type={event.event_type}
      data-event-source={event.event_source}
      data-status={event.status}
      data-synth={event.is_synthesized ? "true" : "false"}
      className="rounded-2xl p-4 transition-colors"
      style={{
        background: "var(--v2-surface)",
        border: "1px solid var(--v2-border)",
        opacity: isDismissed ? 0.55 : 1,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="mt-1 flex-shrink-0 w-2.5 h-2.5 rounded-full"
          style={{ background: type.dotColor }}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              data-testid={`${testidPrefix}-type-chip-${event.id}`}
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
              style={{
                background: type.chipBg,
                border: `1px solid ${type.chipBorder}`,
                color: type.chipText,
              }}
            >
              {type.label}
            </span>
            <span
              data-testid={`${testidPrefix}-source-chip-${event.id}`}
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
              style={{
                background: "rgba(148,163,184,0.06)",
                border: "1px solid var(--v2-border)",
                color: "var(--v2-textMuted)",
              }}
            >
              {src.label}
            </span>
            {event.is_synthesized && (
              <span
                data-testid={`${testidPrefix}-synth-badge-${event.id}`}
                title="Computed from your saved grants + document freshness — not a stored event."
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
                style={{
                  background: "rgba(251,191,36,0.08)",
                  border: "1px dashed rgba(251,191,36,0.55)",
                  color: "rgba(251,191,36,0.9)",
                }}
              >
                Auto
              </span>
            )}
          </div>
          <h3
            data-testid={`${testidPrefix}-title-${event.id}`}
            className="mt-2 text-sm font-semibold text-v2-text truncate"
          >
            {event.title}
          </h3>
          {event.description && (
            <p
              data-testid={`${testidPrefix}-description-${event.id}`}
              className="mt-1 text-xs text-v2-textMuted leading-relaxed"
            >
              {event.description}
            </p>
          )}
          <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] text-v2-textMuted">
            <span className="inline-flex items-center gap-1">
              <CalendarIcon className="w-3 h-3" />
              <span data-testid={`${testidPrefix}-date-${event.id}`}>
                {event.is_synthesized ? "~ " : ""}
                {formatEventDate(event.event_date)}
              </span>
            </span>
            <span
              data-testid={`${testidPrefix}-relative-${event.id}`}
              className="text-v2-textMuted/80"
            >
              {formatRelative(event.event_date)}
            </span>
            {link && (
              <Link
                to={link.to}
                data-testid={`${testidPrefix}-source-link-${event.id}`}
                className="text-v2-accent hover:underline"
              >
                {link.label} →
              </Link>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {event.can_edit && !isDismissed && (
            <button
              type="button"
              onClick={() => onEdit?.(event)}
              data-testid={`${testidPrefix}-edit-${event.id}`}
              title="Edit reminder"
              className="p-1.5 rounded-lg text-v2-textMuted hover:text-v2-text hover:bg-white/[0.03] transition-colors"
              aria-label="Edit"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {event.can_dismiss && !isDismissed && (
            <button
              type="button"
              onClick={() => onDismiss?.(event)}
              data-testid={`${testidPrefix}-dismiss-${event.id}`}
              title="Dismiss this event"
              className="p-1.5 rounded-lg text-v2-textMuted hover:text-v2-text hover:bg-white/[0.03] transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          {event.can_delete && !isDismissed && (
            <button
              type="button"
              onClick={() => onDelete?.(event)}
              data-testid={`${testidPrefix}-delete-${event.id}`}
              title="Delete reminder"
              className="p-1.5 rounded-lg text-red-400/80 hover:text-red-400 hover:bg-red-400/[0.06] transition-colors"
              aria-label="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
